import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";

const NAV = [
  { label: "What We Do", href: "#about" },
  { label: "Process", href: "#process" },
  { label: "Team", href: "/team", route: true },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
  { label: "Blog", href: "/blog", route: true },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  // Force dark/solid style on any page that isn't the landing hero
  const forceSolid = location.pathname !== "/";
  const isSolid = scrolled || forceSolid;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (item) => {
    setOpen(false);
    if (item.route) {
      window.location.href = item.href;
      return;
    }
    const href = item.href;
    if (location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const linkClass = isSolid
    ? "text-[#0F172A] hover:text-[#FF008F]"
    : "text-[#F7F5F0] hover:text-[#FF008F]";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isSolid
          ? "backdrop-blur-xl bg-[#F7F5F0]/90 border-b border-[#E2DDD3]"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="logo">
          <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl overflow-hidden shrink-0 bg-white grid place-items-center transition-shadow ${isSolid ? "shadow-sm ring-1 ring-[#E2DDD3]" : "shadow-lg shadow-black/20"}`}>
            <img
              src="https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/iyaa4h8h_American%20Asset%20logo-01.png"
              alt="American Asset Recovery Agency logo"
              className="w-full h-full object-contain p-1"
            />
          </div>
          <div className="leading-tight hidden sm:block">
            <p className={`font-serif-display text-lg ${isSolid ? "text-[#0F172A]" : "text-[#F7F5F0]"}`}>American Asset</p>
            <p className="font-serif-display text-lg text-[#FF008F] -mt-1">Recovery Agency</p>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => scrollTo(n)}
              className={`text-sm transition-colors ${linkClass}`}
              data-testid={`nav-${n.href.replace(/^[/#]/, "")}`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher dark={!isSolid} />
          <Button
            onClick={() => scrollTo({ href: "#claim" })}
            className="hidden sm:inline-flex bg-[#FF008F] hover:bg-[#D60079] text-white font-bold text-base rounded-full px-6 h-11 transition-all duration-300 hover:-translate-y-0.5"
            data-testid="header-cta"
          >
            Free Claim Check
          </Button>
          <button
            className={`lg:hidden p-2 ${isSolid ? "text-[#0F172A]" : "text-[#F7F5F0]"}`}
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E2DDD3] bg-[#F7F5F0]">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <button
                key={n.href}
                onClick={() => scrollTo(n)}
                className="text-left text-[#0F172A] py-2"
                data-testid={`mnav-${n.href.replace(/^[/#]/, "")}`}
              >
                {n.label}
              </button>
            ))}
            <Button
              onClick={() => scrollTo({ href: "#claim" })}
              className="bg-[#FF008F] hover:bg-[#D60079] text-white font-semibold rounded-full"
            >
              Free Claim Check
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
